import axios from 'axios';
import store from '../store';
import { getToken } from '@/utils/auth';

// 错误日志上报接口（无需登录token，独立axios实例，避免触发request.js里的拦截器造成递归上报）
const logInstance = axios.create();
// 日志接口地址（走平台接口host）
const MONITOR_LOG_URL = `${process.env.VUE_APP_BASE_API}/v1/MonitorLog/AddOrUpdateMonitorLog`;

// 本地开发环境（npm run serve）不上报任何错误日志
const isLocalEnv = process.env.NODE_ENV === 'development';

// 错误类型：1-JS错误 2-API错误 3-资源错误 4-业务错误
const ERROR_TYPE = {
  JS: 1,
  API: 2,
  RESOURCE: 3,
  BUSINESS: 4,
};

// 取第一个非空值（兼容低版本语法，用该方法替代??）
function firstDefined(...args) {
  for (const v of args) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

// 业务模块名称（对应站点标题，不同部署环境按需调整）
const MODULE_NAME = '衡州企服后台';

// 从vuex里取用户信息（store在monitor-log被import时可能尚未初始化，需在上报时动态取）
function getUserId() {
  try {
    const userId = store.getters.userId;
    const orgId = store.getters.defaultorgid;
    return userId || orgId || '';
  } catch (e) {
    return '';
  }
}

// 上报时带上主接口相同的登录信息
logInstance.interceptors.request.use(config => {
  config.headers.tenantId = (store.getters && store.getters.tenantid) || '';
  if (store.getters && store.getters.token) {
    config.headers['X-Token'] = getToken() || '';
  }
  return config;
});

// 根据接口路径拆出功能点（路径最后一段），如 v1/Common/GetJobPosition -> GetJobPosition
function parseUrlSegments(url) {
  try {
    const path = (url || '').replace(/^https?:\/\/[^/]+/i, '');
    const segments = path
      .split('/')
      .map((s) => s.split('?')[0])
      .filter((s) => s && s.toLowerCase() !== 'api' && s.toLowerCase() !== 'v1');
    return segments[segments.length - 1] || '';
  } catch (e) {
    return '';
  }
}

// 格式化本地时间 YYYY-MM-DD HH:mm:ss（toISOString为UTC时间，与本地时区不一致）
function formatLocalTime(date) {
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function toJson(data) {
  if (data === undefined || data === null) return '';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch (e) {
    return '';
  }
}

// 已上报过的API错误本地记录：首次报错上报并记录，后续相同错误不再重复提交
// （轮询类接口30秒请求一次，出错时只上报一次；接口恢复成功后清除记录，再次报错时重新上报）
const reportedApiErrorCache = new Map(); // key: 请求key，value: Set(错误key)

// 请求key：请求方式+地址+参数，标识同一个请求（轮询请求每次调用的key一致）
function getRequestKey(config) {
  return `${(config && config.method) || ''}|${(config && config.url) || ''}|${toJson((config && config.data) || (config && config.params))}`;
}

// 错误key：错误码+错误信息，同一请求不同的错误可分别上报一次
function getErrorKey(errorMsg, errorCode) {
  return `${errorCode}|${errorMsg}`;
}

function isApiErrorReported(requestKey, errorKey) {
  const set = reportedApiErrorCache.get(requestKey);
  return !!set && set.has(errorKey);
}

function recordApiError(requestKey, errorKey) {
  let set = reportedApiErrorCache.get(requestKey);
  if (!set) {
    set = new Set();
    reportedApiErrorCache.set(requestKey, set);
  }
  set.add(errorKey);
  // 防止长时间打开的页面记录无限增长
  if (reportedApiErrorCache.size > 500) reportedApiErrorCache.clear();
}

// 接口请求成功后清除该请求的错误记录（下次再报错时重新上报）
export function clearApiErrorRecord(config = {}) {
  reportedApiErrorCache.delete(getRequestKey(config));
}

// 获取浏览器名称和版本
function getBrowserInfo() {
  const ua = navigator.userAgent;
  let match;
  // Edge（新版Edg/、旧版Edge/，需在Chrome之前检测）
  if ((match = ua.match(/(Edg|Edge)\/([\d.]+)/))) return { name: 'Edge', version: match[2] };
  // Opera（OPR/，需在Chrome之前检测）
  if ((match = ua.match(/OPR\/([\d.]+)/))) return { name: 'Opera', version: match[1] };
  // IE（document.documentMode仅IE支持）
  if (document.documentMode) return { name: 'IE', version: String(document.documentMode) };
  // Firefox
  if ((match = ua.match(/Firefox\/([\d.]+)/i))) return { name: 'Firefox', version: match[1] };
  // Safari（UA含Version/x.y + Safari，Chrome和Edge的UA不含Version/）
  if ((match = ua.match(/Version\/([\d.]+).*Safari/i))) return { name: 'Safari', version: match[1] };
  // 移动端Chrome（iOS）
  if ((match = ua.match(/CriOS\/([\d.]+)/))) return { name: 'Chrome', version: match[1] };
  // Chrome
  if (window.chrome !== undefined && (match = ua.match(/Chrome\/([\d.]+)/))) return { name: 'Chrome', version: match[1] };
  return { name: 'Unknown', version: '未知' };
}
// 浏览器信息模块加载时获取一次即可
const browserInfo = getBrowserInfo();

/**
 * 上报错误日志
 * @param {Object} info
 * @param {Number} info.errorType 错误类型：1-JS错误 2-API错误 3-资源错误 4-业务错误
 * @param {Object} [info.config] 请求配置（API错误时传，取url、请求参数等）
 * @param {Object} [info.error] 错误对象（axios错误或业务返回体）
 * @param {String} [info.extra] 扩展数据（不传时按错误对象自动提取）
 */
export function reportError(info = {}) {
  // 本地环境不上报
  if (isLocalEnv) return;
  try {
    const { errorType = ERROR_TYPE.API, config = {}, error = {} } = info;
    // 接口报错reject后若未被业务捕获，会以unhandledrejection等方式抛到控制台，
    // 已按API错误上报过的错误对象不再按JS错误重复上报
    if (errorType === ERROR_TYPE.JS && isReportedApiError(error)) return;
    // JS错误内容为Network Error的不上报（多为断网导致）
    if (errorType === ERROR_TYPE.JS && isNetworkError(error)) return;
    const errorTime = formatLocalTime(new Date()); // 本地当前日期+时间
    const url = (config && config.url) || (error && error.config && error.config.url);
    const feature = parseUrlSegments(url);
    // 扩展数据：优先使用传入的，否则HTTP报错取响应体，业务报错取整个返回体
    const extra = info.extra !== undefined && info.extra !== null ? info.extra : ((error.response && error.response.data)
      ? toJson(error.response.data)
      : (error.IsSuccess !== undefined || error.Code !== undefined || error.Message !== undefined ? toJson(error) : ''));
    const errorMsg = (error.data && error.data.message) || error.Message || error.message || error.msg || toJson(error);
    const errorCode = String(firstDefined(error.Code, error.code, error.status, error.Status, error.response && error.response.status));
    const requestJson = toJson(config.data || config.params);
    // API错误本地去重：首次报错上报并记录，后续相同错误不再重复提交
    if (errorType === ERROR_TYPE.API) {
      const requestKey = getRequestKey(config);
      const errorKey = getErrorKey(errorMsg, errorCode);
      if (isApiErrorReported(requestKey, errorKey)) return;
      recordApiError(requestKey, errorKey);
    }
    const payload = {
      platform: 1, // 平台：1-Web
      module: MODULE_NAME, // 业务模块：衡州企服
      feature,
      pageUrl: window.location.hash, // 页面路径
      apiUrl: config.url || '', // API路径
      errorType,
      errorMsg,
      errorCode,
      errorStack: extra || error.stack || '', // 错误堆栈
      requestJson,
      completeTime: errorTime, // 接口完成时间（报错即完成）
      duration: config.metadata && config.metadata.startTime ? Date.now() - config.metadata.startTime : 0, // 耗时(ms)
      userId: getUserId(), // 用户ID
      browser: `${browserInfo.name} ${browserInfo.version}`, // 浏览器
    };

    // 上报失败不影响业务，静默处理
    logInstance
      .post(MONITOR_LOG_URL, payload)
      .catch(() => {});
  } catch (e) {
    // 日志上报本身出错时忽略
  }
}

// 相同错误10秒内去重，避免同一错误反复上报刷屏
const reportedCache = new Map();
function isRepeated(key) {
  if (!key) return false;
  const now = Date.now();
  if (reportedCache.has(key) && now - reportedCache.get(key) < 10000) {
    return true;
  }
  if (reportedCache.size > 200) reportedCache.clear();
  reportedCache.set(key, now);
  return false;
}

// 已按API错误上报过的错误对象（WeakSet弱引用，不阻止对象回收）
const reportedApiErrors = new WeakSet();

// 判断错误是否已作为API错误上报过
function isReportedApiError(error) {
  return error !== null && (typeof error === 'object' || typeof error === 'function') && reportedApiErrors.has(error);
}

/** 上报JS错误（带10秒去重） */
export function reportJsError(error, extra) {
  // API错误被reject后抛到控制台，会被全局监听再次捕获，这里跳过避免重复上报
  if (isReportedApiError(error)) return;
  const signature = `${(error && error.message) || (error && error.Message) || ''}@${(error && error.stack) || ''}`;
  if (isRepeated(signature)) return;
  reportError({
    errorType: ERROR_TYPE.JS,
    error: error || {},
    extra,
  });
}

// 判断是否为网络类错误（断网/接口不通）：axios错误码ERR_NETWORK，或错误内容为Network Error
function isNetworkError(error) {
  if (!error) return false;
  const code = firstDefined(error.Code, error.code);
  if (String(code) === 'ERR_NETWORK') return true;
  const msg = typeof error === 'string' ? error : (firstDefined(error.Message, error.message) || '');
  return String(msg) === 'Network Error';
}

// 判断是否为指定状态码的错误（102-ArgumentError参数错误 103-NeedLogin需要登录 400-BadRequest 403-无权限，不上报）
function isIgnoreStatusError(error) {
  if (!error) return false;
  const code = firstDefined(
    error.Code,
    error.code,
    error.status,
    error.Status,
    error.response && error.response.data && error.response.data.code,
    error.response && error.response.status
  );
  const codeStr = String(code);
  return codeStr === '102' || codeStr === '103' || codeStr === '400' || codeStr === '403';
}

// 判断是否为登录失效类错误（401/50008/50012/50014、请登录后再访问等）
function isAuthError(error) {
  const code = firstDefined(
    error && error.Code,
    error && error.code,
    error && error.response && error.response.data && error.response.data.code,
    error && error.status,
    error && error.Status,
    error && error.response && error.response.status
  );
  if (['401', '50008', '50012', '50014'].includes(String(code))) return true;
  const msg = firstDefined(
    error && error.Message,
    error && error.message,
    error && error.response && error.response.data && error.response.data.message
  ) || '';
  return String(msg).includes('请登录') || String(msg).includes('登录已失效') || String(msg).includes('登录失效') || String(msg).includes('登录已超时') || String(msg).includes('未授权');
}

/**
 * 上报API错误
 * 登录失效（401/请登录）类错误不上报，避免登录过期时批量接口同时报错刷屏
 */
export function reportApiError(error, config = {}) {
  // 标记为API错误（即使被10秒去重跳过也标记），该错误对象后续再被全局监听捕获时不按JS错误上报
  if (error !== null && (typeof error === 'object' || typeof error === 'function')) {
    reportedApiErrors.add(error);
  }
  // 登录失效类错误（401/50008/50012/50014等）不进行报错上报
  if (isAuthError(error)) return;
  // 网络错误（错误码ERR_NETWORK，断网/接口不通）不上报
  if (isNetworkError(error)) return;
  // axios错误码ERR_BAD_REQUEST（HTTP 400请求错误）不上报
  if (String(firstDefined(error && error.Code, error && error.code)) === 'ERR_BAD_REQUEST') return;
  // 状态码102（参数错误）/103（需要登录）/400（请求错误）/403（无权限）不上报
  if (isIgnoreStatusError(error)) return;
  reportError({
    errorType: ERROR_TYPE.API,
    config,
    error,
  });
}

/** 注册全局JS错误、资源错误、未处理Promise异常监听（重复调用只会注册一次） */
export function initErrorMonitor() {
  // 本地环境不注册监听、不上报
  if (isLocalEnv) return;
  if (window.__errorMonitorInit) return;
  window.__errorMonitorInit = true;

  // JS运行时错误
  window.addEventListener('error', (event) => {
    if (!(event instanceof ErrorEvent) || !event.error) return;
    reportJsError(event.error);
  });

  // 资源加载错误（图片、脚本等不会冒泡，需在捕获阶段监听）
  window.addEventListener('error', (event) => {
    // 暂时取消资源类型错误上报（后续需要时放开此return）
    return;
    const target = event.target;
    if (!target || target === window || !target.nodeName) return;
    if (!['IMG', 'SCRIPT', 'LINK', 'VIDEO', 'AUDIO', 'SOURCE', 'IFRAME'].includes(target.nodeName)) return;
    // 登录页（#/login）的资源加载错误不上报（多为未登录状态加载静态资源失败，非业务缺陷）
    if (String(window.location.hash).indexOf('#/login') === 0) return;
    const url = target.src || target.href || '';
    if (isRepeated(url)) return;
    reportError({
      errorType: ERROR_TYPE.RESOURCE,
      config: { url }, // 资源地址，module/feature按路径拆出
      error: { message: `资源加载失败: ${url}` },
    });
  }, true); // 捕获阶段

  // 未处理的Promise异常
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const signature = `${(reason && reason.message) || (reason && reason.Message) || reason}@${(reason && reason.stack) || ''}`;
    if (isRepeated(signature)) return;
    reportError({
      errorType: ERROR_TYPE.JS,
      error: reason != null ? reason : {},
    });
  });
}

export { ERROR_TYPE, MONITOR_LOG_URL };
